import type { Langue } from '../i18n'
import { TEXTES } from '../i18n'
import type { AvisPublie, CritereAvis } from '../apiReputation'
import { Etoiles } from './Etoiles'

/**
 * Carte d'avis — le second motif attendu par le §6 de la conception L10.
 *
 * **Elle montre la durée de la relation à côté de la note, et c'est délibéré** (conception L8,
 * §1.5). Il n'y a pas de durée minimale pour déposer un avis : un séjour terminé au bout de trois
 * jours est exactement l'information qu'un futur locataire veut lire. On informe le lecteur au lieu
 * de censurer l'auteur — « a séjourné 3 jours » à côté d'une note de 1/5 se lit tout seul.
 *
 * **Le commentaire arrive déjà caviardé** : la rédaction des coordonnées est une projection côté
 * serveur (D26), jamais une écriture. Ce composant n'a rien à masquer lui-même — et ne doit surtout
 * pas essayer, sous peine de faire diverger deux détecteurs.
 *
 * **Aucune chaîne dans le JSX** (règle 2) : tout passe par `TEXTES`, y compris les libellés de
 * critères, qui viennent du référentiel serveur en trois langues.
 */

interface CarteAvisProps {
  avis: AvisPublie
  langue: Langue
  criteres: CritereAvis[]
  /** Rendu quand l'utilisateur courant est le sujet et n'a pas encore répondu. */
  actions?: React.ReactNode
}

export function CarteAvis({ avis, langue, criteres, actions }: CarteAvisProps) {
  const t = TEXTES[langue]

  const dateFormat = new Intl.DateTimeFormat(langue, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })

  const nombreFormat = new Intl.NumberFormat(langue)

  const jours = dureeEnJours(avis.relationStartedOn, avis.relationEndedOn)

  return (
    <article className="carte-avis">
      <header className="carte-avis__entete">
        <Etoiles note={avis.overallRating} langue={langue} libelle={t.avisReputation} />
        <span className="carte-avis__auteur">{avis.authorDisplayName ?? '—'}</span>
        <time dateTime={avis.publishedAt}>{dateFormat.format(new Date(avis.publishedAt))}</time>
      </header>

      {jours !== null && (
        <p className="carte-avis__duree">
          {t.avisDureeRelation} : {nombreFormat.format(jours)} {t.avisDureeJours}
        </p>
      )}

      {avis.comment && <p className="carte-avis__texte">{avis.comment}</p>}

      {avis.scores.length > 0 && (
        <ul className="carte-avis__criteres">
          {avis.scores.map((score) => (
            <li key={score.criterionCode}>
              <span>{libelleCritere(criteres, score.criterionCode, langue)}</span>
              <Etoiles
                note={score.score}
                langue={langue}
                libelle={libelleCritere(criteres, score.criterionCode, langue)}
              />
            </li>
          ))}
        </ul>
      )}

      {avis.response && (
        <aside className="carte-avis__reponse">
          <h4>{t.avisReponse}</h4>
          <p>{avis.response.body}</p>
          <time dateTime={avis.response.publishedAt}>
            {dateFormat.format(new Date(avis.response.publishedAt))}
          </time>
        </aside>
      )}

      {actions && <footer className="carte-avis__actions">{actions}</footer>}
    </article>
  )
}

/**
 * Le libellé du référentiel, dans la langue d'interface.
 *
 * Le code est rendu en dernier recours plutôt qu'une chaîne vide : un critère ajouté côté serveur et
 * pas encore connu du front doit rester identifiable, pas disparaître.
 */
export function libelleCritere(
  criteres: CritereAvis[],
  code: string,
  langue: Langue,
): string {
  const critere = criteres.find((entree) => entree.code === code)

  if (!critere) {
    return code
  }

  if (langue === 'ar') {
    return critere.labelAr
  }

  // Le referentiel ne porte que deux libelles : l'anglais retombe sur le francais, qui reste
  // lisible, plutot que sur un code technique.
  return critere.labelFr
}

function dureeEnJours(debut: string, fin: string | null): number | null {
  if (!fin) {
    return null
  }

  const jours = Math.round(
    (new Date(fin).getTime() - new Date(debut).getTime()) / (24 * 60 * 60 * 1000),
  )

  return jours >= 0 ? jours : null
}
